import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Image,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { useNavigation } from '@react-navigation/native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useAuth } from '../context/AuthContext';
import { cxcRegistrationAPI } from '../services/api';
import { CxcRegistration, CxcRegistrationReceipt, CxcSubject } from '../types';
import { WebPageShell } from '../components/common/WebPageShell';
import { Toast } from '../components/common/Toast';
import { SubmittingOverlay } from '../components/common/SubmittingOverlay.web';
import { Spacing, FontSize, BorderRadius } from '../theme/colors';
import { useTheme, ThemeColors } from '../context/ThemeContext';
import { useStudentProfilePhoto } from '../hooks/useStudentProfilePhoto';
import { Logger } from '../utils/logger';
import { extractApiErrorMessage } from '../utils/earlyPickup';
import { printRegistrationForm } from '../utils/printRegistrationForm';
import CxcPrintDocument, { CxcPreviewModal, CxcStatusPill } from '../components/cxc/CxcPrintDocument.web';
import { useCxcOptions } from '../hooks/useCxcOptions';
import {
  CXC_SBA_FAQ_URL,
  CxcFormState,
  cxcCentre,
  cxcFees,
  cxcSittings,
  CxcSubjectSelection,
  EMPTY_CXC_FORM,
  buildCxcInvoiceLines,
  candidateFullName,
  estimateCxcTotal,
  formatMoney,
  isSittingOpen,
  nextOpenCxcSitting,
  normalizeDateInput,
  prefillFromSnapshot,
  selectedSubjectChoices,
  toggleSubject,
  validateCxcForm,
  cxcStatusLabel,
  isStudentEligibleForCxc,
  looksLikePortraitPhoto,
  PORTRAIT_PHOTO_WARNING,
  IDENTIFICATION_TYPES,
  EMPTY_IDENTIFICATION,
  IDENTIFICATION_MAX_BYTES,
  IDENTIFICATION_PDF_PREFIX,
  sanitizeIdentificationNumber,
  sanitizeCountry,
  todayIsoDate,
  dataUrlSizeLabel,
  identificationKind,
  splitFileName,
  validateIdentification,
  type IdentificationInput,
} from '../utils/cxcRegistration';

const BANNER_IMAGE = require('../assets/images/banner_img.png');

type TabType = 'form' | 'receipts';
type SubjectFilter = 'ALL' | 'GENERAL' | 'CAPE' | 'SELECTED';

// This file is web-only (react-native-web). A plain string handed to JSX renders as
// that DOM tag, which is what gives us the browser's own date/file inputs and a
// real <canvas> to draw a signature on - the same trick EarlyPickupScreen.web.tsx
// uses for <input type="date">.
const WebInput: any = 'input';
const WebCanvas: any = 'canvas';
const WebAnchor: any = 'a';

const webFieldStyle = (colors: ThemeColors, hasError: boolean): Record<string, any> => ({
  height: 44,
  width: '100%',
  boxSizing: 'border-box',
  padding: '0 12px',
  borderRadius: 8,
  border: `1px solid ${hasError ? colors.danger : colors.border}`,
  background: hasError ? colors.dangerLight : colors.surface,
  color: colors.textPrimary,
  fontSize: 14,
  fontFamily: 'inherit',
});

/**
 * A minimal drawing pad on a DOM canvas. `toDataURL()` yields the PNG the backend
 * stores; `isEmpty()` says whether anything was drawn at all.
 */
interface SignaturePadHandle {
  toDataURL: () => string | null;
  isEmpty: () => boolean;
  clear: () => void;
}

const SignaturePad = React.forwardRef<SignaturePadHandle, { colors: ThemeColors; hasError?: boolean }>(
  ({ colors, hasError }, ref) => {
    // `any`: this RN tsconfig has no DOM lib, and the canvas only exists on web.
    const canvasRef = useRef<any>(null);
    const drawing = useRef(false);
    const dirty = useRef(false);

    const point = (e: any) => {
      const canvas = canvasRef.current!;
      const rect = canvas.getBoundingClientRect();
      return {
        x: ((e.clientX - rect.left) / rect.width) * canvas.width,
        y: ((e.clientY - rect.top) / rect.height) * canvas.height,
      };
    };

    const start = (e: any) => {
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      drawing.current = true;
      dirty.current = true;
      const { x, y } = point(e);
      ctx.strokeStyle = '#0a3d62';
      ctx.lineWidth = 2.2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(x, y);
      e.target.setPointerCapture?.(e.pointerId);
    };

    const move = (e: any) => {
      if (!drawing.current) return;
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      const { x, y } = point(e);
      ctx.lineTo(x, y);
      ctx.stroke();
    };

    const end = () => { drawing.current = false; };

    React.useImperativeHandle(ref, () => ({
      toDataURL: () => (dirty.current && canvasRef.current ? canvasRef.current.toDataURL('image/png') : null),
      isEmpty: () => !dirty.current,
      clear: () => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
        dirty.current = false;
      },
    }));

    return (
      <WebCanvas
        ref={canvasRef}
        width={800}
        height={220}
        style={{
          width: '100%',
          height: 160,
          display: 'block',
          borderRadius: 10,
          border: `1px dashed ${hasError ? colors.danger : colors.textMuted}`,
          background: '#FAFCFF',
          touchAction: 'none',
          cursor: 'crosshair',
        }}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        onPointerCancel={end}
      />
    );
  },
);
SignaturePad.displayName = 'SignaturePad';

/** Same form as the native screen, laid out for a desktop browser (see CxcRegistrationScreen.tsx). */
export function CxcRegistrationScreen() {
  const { selectedStudent } = useAuth();
  const navigation = useNavigation<any>();
  const { colors, isDark } = useTheme();
  const { width } = useWindowDimensions();
  const isMobile = width < 860;
  const styles = useMemo(() => createStyles(colors, isDark, isMobile), [colors, isDark, isMobile]);

  const [tab, setTab] = useState<TabType>('form');
  const [form, setForm] = useState<CxcFormState>(EMPTY_CXC_FORM);
  const [subjects, setSubjects] = useState<CxcSubject[]>([]);
  const [subjectsLoading, setSubjectsLoading] = useState(true);
  const [selection, setSelection] = useState<CxcSubjectSelection>({});
  const [subjectFilter, setSubjectFilter] = useState<SubjectFilter>('ALL');
  const [subjectSearch, setSubjectSearch] = useState('');
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null>(null);
  // The full identification document: a separate image from the passport-size photo above.
  const [identificationDataUrl, setIdentificationDataUrl] = useState<string | null>(null);
  const [identificationFileName, setIdentificationFileName] = useState<string | null>(null);
  // The chosen document's own details: its type, number, country (a passport) and expiry.
  const [identification, setIdentification] = useState(EMPTY_IDENTIFICATION);
  // A hint, never a blocker: the image may be a portrait photo rather than the whole document.
  const [identificationWarning, setIdentificationWarning] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState<CxcRegistrationReceipt | null>(null);
  const [registrations, setRegistrations] = useState<CxcRegistration[]>([]);
  const [registrationsLoading, setRegistrationsLoading] = useState(false);
  const [showPosterModal, setShowPosterModal] = useState(false);
  // Review & Print popup shown on "Submit", the same Save & Preview step the
  // public website's own CXC form uses - the parent can print the form here
  // before it is actually sent.
  const [showPreview, setShowPreview] = useState(false);
  const [candidateSigDataUrl, setCandidateSigDataUrl] = useState<string | null>(null);
  const [parentSigDataUrl, setParentSigDataUrl] = useState<string | null>(null);
  const [toast, setToast] = useState<{ visible: boolean; message: string; type: 'success' | 'error' }>({
    visible: false, message: '', type: 'success',
  });
  // Drawn state is tracked in the pad refs; this counter only re-renders the error hint.
  const [signatureVersion, setSignatureVersion] = useState(0);

  const candidatePad = useRef<SignaturePadHandle>(null);
  const parentPad = useRef<SignaturePadHandle>(null);
  const previewDocRef = useRef<any>(null);
  const successDocRef = useRef<any>(null);
  // Shown inline in the review popup, the way the public website reports a failed submit.
  const [submitError, setSubmitError] = useState<string | null>(null);
  // A past registration opened from the Previous tab - same document, read-only.
  const [viewing, setViewing] = useState<CxcRegistration | null>(null);
  const viewDocRef = useRef<any>(null);

  // Read the student's existing profile photo if one is on record, offering 1-click import
  const { displayPhoto } = useStudentProfilePhoto(selectedStudent?.id || '', selectedStudent || undefined);

  const showToast = (message: string, type: 'success' | 'error' = 'success') =>
    setToast({ visible: true, message, type });

  // Sittings, deadlines and fees from the server (the one copy, also what it
  // checks the submit against); same names the screen always used.
  const { options: cxcOptions, error: cxcOptionsError } = useCxcOptions();
  const CXC_SITTINGS = useMemo(() => cxcSittings(cxcOptions), [cxcOptions]);
  const CXC_FEES = useMemo(() => cxcFees(cxcOptions), [cxcOptions]);
  const CXC_CENTRE = useMemo(() => cxcCentre(cxcOptions), [cxcOptions]);
  const OPEN_SITTINGS = useMemo(() => CXC_SITTINGS.filter(s => s.isOpen), [CXC_SITTINGS]);

  // Open/closed per sitting, exactly as the server reports it (Cayman time).
  const sittingsWithStatus = CXC_SITTINGS;

  const allSittingsClosed = useMemo(() => {
    return !sittingsWithStatus.some(s => s.isOpen);
  }, [sittingsWithStatus]);

  const hasAnyClosedSitting = useMemo(() => {
    return sittingsWithStatus.some(s => !s.isOpen);
  }, [sittingsWithStatus]);

  // Pre-fill everything the school already holds for the pupil (the /Parent
  // snapshot the app keeps) so the parent confirms rather than re-types.
  useEffect(() => {
    if (!selectedStudent) return;
    setForm(prev => {
      const prefilled = prefillFromSnapshot(selectedStudent);
      const nextOpen = nextOpenCxcSitting(CXC_SITTINGS);
      // Keep a still-open choice; never leave a closed sitting selected.
      const defaultSitting = prev.sitting && isSittingOpen(CXC_SITTINGS, prev.sitting)
        ? prev.sitting
        : (nextOpen ? nextOpen.id : '');
      return { ...prev, ...prefilled, sitting: defaultSitting };
    });
  }, [selectedStudent, CXC_SITTINGS]);

  useEffect(() => {
    let cancelled = false;
    cxcRegistrationAPI.getSubjects()
      .then(res => { if (!cancelled) setSubjects(res.data ?? []); })
      .catch(err => {
        Logger.error('CxcRegistration', 'Failed to load the subject list', err);
        if (!cancelled) showToast(extractApiErrorMessage(err, 'The subject list could not be loaded.'), 'error');
      })
      .finally(() => { if (!cancelled) setSubjectsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const fetchRegistrations = useCallback(async () => {
    if (!selectedStudent) return;
    setRegistrationsLoading(true);
    try {
      const res = await cxcRegistrationAPI.getMyChildRegistrations(selectedStudent.id);
      setRegistrations(res.data ?? []);
    } catch (err) {
      Logger.error('CxcRegistration', 'Failed to load previous registrations', err);
      showToast(extractApiErrorMessage(err, 'Previous registrations could not be loaded.'), 'error');
    } finally {
      setRegistrationsLoading(false);
    }
  }, [selectedStudent]);

  useEffect(() => { fetchRegistrations(); }, [fetchRegistrations]);

  const setField = (name: keyof CxcFormState, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => { const next = { ...prev }; delete next[name]; return next; });
  };

  const selectedSubjects = useMemo(() => subjects.filter(s => selection[s.name]?.selected), [subjects, selection]);

  const visibleSubjects = useMemo(() => {
    let list = subjects;
    if (subjectFilter === 'GENERAL') list = subjects.filter(s => s.proficiency === 'GENERAL');
    if (subjectFilter === 'CAPE') list = subjects.filter(s => s.proficiency === 'CAPE');
    if (subjectFilter === 'SELECTED') list = selectedSubjects;
    const q = subjectSearch.trim().toLowerCase();
    return q ? list.filter(s => s.name.toLowerCase().includes(q)) : list;
  }, [subjects, subjectFilter, subjectSearch, selectedSubjects]);

  const onToggleSubject = (name: string, field: 'selected' | 'resit') => {
    setSelection(prev => toggleSubject(prev, name, field));
    if (errors.subjects) setErrors(prev => { const next = { ...prev }; delete next.subjects; return next; });
  };

  const onPhotoChange = (e: any) => {
    const file: File | undefined = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { showToast('Please choose an image file (JPG or PNG).', 'error'); return; }
    if (file.size > 5 * 1024 * 1024) { showToast('Please choose an image smaller than 5MB.', 'error'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setPhotoDataUrl(reader.result as string);
      if (errors.photo) setErrors(prev => { const next = { ...prev }; delete next.photo; return next; });
    };
    reader.readAsDataURL(file);
  };

  const clearIdentificationError = (key: string) =>
    setErrors(prev => { if (!prev[key]) return prev; const next = { ...prev }; delete next[key]; return next; });

  const setIdentificationDetail = (patch: Partial<IdentificationInput>) => {
    setIdentification(prev => ({ ...prev, ...patch } as typeof prev));
    Object.keys(patch).forEach(k => clearIdentificationError(`identification${k.charAt(0).toUpperCase()}${k.slice(1)}`));
  };

  const onIdentificationChange = (e: any) => {
    const file: File | undefined = e.target.files?.[0];
    if (!file) return;
    const accepted = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!accepted.includes(file.type)) { showToast('Please choose a PDF, JPG or PNG file.', 'error'); return; }
    if (file.size > IDENTIFICATION_MAX_BYTES) { showToast('Please choose a file smaller than 5MB.', 'error'); return; }
    setIdentificationWarning(null);
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setIdentificationDataUrl(dataUrl);
      setIdentificationFileName(file.name);
      const ImageCtor = (globalThis as any).Image;
      if (ImageCtor && file.type !== 'application/pdf') {
        const probe = new ImageCtor();
        probe.onload = () => {
          if (looksLikePortraitPhoto(probe.naturalWidth, probe.naturalHeight)) setIdentificationWarning(PORTRAIT_PHOTO_WARNING);
        };
        probe.src = dataUrl;
      }
      clearIdentificationError('identificationDocument');
    };
    reader.readAsDataURL(file);
  };

  const handleChooseIdentificationClick = () => {
    const docObj = (globalThis as any).document;
    if (!docObj?.createElement) return;
    const input = docObj.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp,application/pdf';
    input.onchange = onIdentificationChange;
    input.click();
  };

  // A styled button triggering a hidden file input, the same technique
  // HomeScreen.web.tsx uses for its own photo upload - a raw <input type=file>
  // renders as the browser's own unstyled control, which clashes with
  // everything else on this form.
  const handleChoosePhotoClick = () => {
    const docObj = (globalThis as any).document;
    if (!docObj?.createElement) return;
    const input = docObj.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = onPhotoChange;
    input.click();
  };

  const estimatedTotal = estimateCxcTotal(CXC_FEES, selectedSubjects.length);
  const invoiceLines = buildCxcInvoiceLines(CXC_FEES, selectedSubjects.length);
  const name = candidateFullName(form);
  // One ref per mounted copy. The review popup's copy and the success card's
  // copy overlap briefly (RN Modal keeps its children through the fade-out),
  // and a single shared ref ends up null once the popup's copy finally
  // unmounts - Print would then fall back to printing the whole page.
  const renderPrintDocument = (ref: React.MutableRefObject<any>) => (
    <CxcPrintDocument
      ref={ref}
      form={form}
      centre={CXC_CENTRE}
      sittings={CXC_SITTINGS}
      fees={CXC_FEES}
      selectedSubjects={selectedSubjects}
      selection={selection}
      photoDataUrl={photoDataUrl}
      candidateSignatureData={candidateSigDataUrl}
      parentSignatureData={parentSigDataUrl}
      estimatedTotal={estimatedTotal}
      receipt={receipt}
    />
  );

  // The server stores images as bare base64 (no data-URI prefix); <img> needs one.
  const asDataUrl = (b64: string | null | undefined, mime: string) =>
    !b64 ? null : b64.startsWith('data:') ? b64 : `data:${mime};base64,${b64}`;

  /** A past registration as the same document the form previews - every field it was submitted with. */
  const viewingDocument = useMemo(() => {
    if (!viewing) return null;
    const r = viewing;
    const sittingId = `${r.sittingMonth}-${r.sittingYear}`;
    const own = {
      id: sittingId,
      key: r.sittingMonth as 'January' | 'MayJune',
      label: `${r.sittingMonth === 'MayJune' ? 'May/June' : r.sittingMonth} ${r.sittingYear}`,
      year: r.sittingYear,
      deadline: '',
      isOpen: false,
      daysRemaining: 0,
    };
    const docForm: CxcFormState = {
      sitting: sittingId,
      gradeOrClass: r.gradeOrClass ?? '',
      firstName: r.firstName,
      middleName: r.middleName ?? '',
      lastName: r.lastName,
      previousRegistrationNumber: r.previousRegistrationNumber ?? '',
      dateOfBirth: (r.dateOfBirth ?? '').slice(0, 10),
      gender: r.gender === 'Female' || r.gender === 'Male' ? r.gender : '',
      address: r.address ?? '',
      homePhone: r.homePhone ?? '',
      cellPhone: r.cellPhone ?? '',
      email: r.email ?? '',
      parentGuardianName: r.parentGuardianName ?? '',
    };
    return (
      <CxcPrintDocument
        ref={viewDocRef}
        form={docForm}
        centre={CXC_CENTRE}
        // Its own sitting may have closed and dropped off the server's list; it must still show ticked.
        sittings={CXC_SITTINGS.some(s => s.id === sittingId) ? CXC_SITTINGS : [...CXC_SITTINGS, own]}
        fees={CXC_FEES}
        selectedSubjects={r.subjects.map((s, i) => ({ id: `${s.subjectName}-${i}`, name: s.subjectName, proficiency: s.proficiency, sbaRequiredElsewhere: false, sortOrder: i }))}
        selection={Object.fromEntries(r.subjects.map(s => [s.subjectName, { selected: true, resit: s.isResit }]))}
        photoDataUrl={asDataUrl(r.photo, 'image/jpeg')}
        candidateSignatureData={asDataUrl(r.candidateSignature, 'image/png')}
        parentSignatureData={asDataUrl(r.parentSignature, 'image/png')}
        estimatedTotal={r.estimatedTotalCost}
        receipt={{ id: r.id, invoiceNumber: r.invoiceNumber, estimatedTotalCost: r.estimatedTotalCost }}
      />
    );
  }, [viewing, CXC_CENTRE, CXC_SITTINGS, CXC_FEES]);

  /** "Submit CXC Registration" - validates, snapshots both signatures, then opens the review/print popup. Nothing is sent yet. */
  const handleReviewAndPrint = () => {
    if (!selectedStudent) return;
    const validation = { ...validateCxcForm(form, {
      hasStudent: true,
      selectedSubjectCount: selectedSubjects.length,
      hasPhoto: !!photoDataUrl,
      hasCandidateSignature: !(candidatePad.current?.isEmpty() ?? true),
      sittings: CXC_SITTINGS,
      maxSubjects: CXC_FEES.maxSubjects,
      studentClass: selectedStudent.class,
    }), ...validateIdentification({ ...identification, hasDocument: !!identificationDataUrl }) };
    setErrors(validation);
    setSignatureVersion(v => v + 1);
    if (Object.keys(validation).length > 0) {
      showToast('Please fix the highlighted fields.', 'error');
      return;
    }

    if (form.sitting && !isSittingOpen(CXC_SITTINGS, form.sitting)) {
      const sittingObj = CXC_SITTINGS.find(s => s.id === form.sitting);
      showToast(
        `Registration for ${sittingObj?.label || form.sitting} closed on ${sittingObj?.deadline}. Submissions are no longer accepted for this session.`,
        'error',
      );
      return;
    }

    const candidateSignature = candidatePad.current?.toDataURL() ?? null;
    if (!candidateSignature) return;
    setCandidateSigDataUrl(candidateSignature);
    setParentSigDataUrl(parentPad.current?.toDataURL() ?? null);
    setShowPreview(true);
  };

  /** "Confirm & Submit" on the review popup - the actual API call, using the signatures captured when the popup opened. */
  const handleConfirmSubmit = async () => {
    if (!selectedStudent || !candidateSigDataUrl) return;
    const sitting = CXC_SITTINGS.find(s => s.id === form.sitting)!;
    const today = new Date().toISOString().slice(0, 10);

    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await cxcRegistrationAPI.submit({
        studentId: selectedStudent.id,
        gradeOrClass: form.gradeOrClass.trim().toUpperCase() || null,
        sittingMonth: sitting.key,
        sittingYear: sitting.year,
        firstName: form.firstName.trim().toUpperCase(),
        lastName: form.lastName.trim().toUpperCase(),
        middleName: form.middleName.trim().toUpperCase() || null,
        previousRegistrationNumber: form.previousRegistrationNumber.trim().toUpperCase() || null,
        dateOfBirth: normalizeDateInput(form.dateOfBirth)!,
        gender: form.gender as 'Female' | 'Male',
        address: form.address.trim().toUpperCase(),
        homePhone: form.homePhone.trim() || null,
        cellPhone: form.cellPhone.trim(),
        email: form.email.trim(),
        photo: photoDataUrl!,
        identificationDocument: identificationDataUrl,
        identificationType: identification.type || null,
        identificationNumber: identification.number.trim() || null,
        identificationCountry: identification.type === 'PASSPORT' ? identification.country.trim() || null : null,
        identificationExpiry: normalizeDateInput(identification.expiry),
        subjects: selectedSubjectChoices(subjects, selection),
        candidateSignature: candidateSigDataUrl,
        candidateSignatureDate: today,
        parentGuardianName: form.parentGuardianName.trim().toUpperCase() || null,
        parentSignature: parentSigDataUrl,
        parentSignatureDate: parentSigDataUrl ? today : null,
      });
      setReceipt(res.data);
      setShowPreview(false);
      showToast(`Registration received. Invoice ${res.data.invoiceNumber} emailed to ${form.email.trim()}.`);
      fetchRegistrations();
    } catch (err) {
      Logger.error('CxcRegistration', 'Failed to submit the registration', err);
      setSubmitError(extractApiErrorMessage(err, 'The registration could not be submitted. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrint = () =>
    printRegistrationForm(
      successDocRef.current ?? previewDocRef.current,
      `CSEC Registration Form - ${name || selectedStudent?.name || 'Cayman Academy'}`,
    );

  const handlePrintViewing = () =>
    printRegistrationForm(viewDocRef.current, `CSEC Registration Form - ${viewing ? `${viewing.firstName} ${viewing.lastName}` : 'Cayman Academy'}`);

  const startAnother = () => {
    setReceipt(null);
    setSelection({});
    setPhotoDataUrl(null);
    setIdentificationDataUrl(null);
    setIdentificationFileName(null);
    setIdentification(EMPTY_IDENTIFICATION);
    setIdentificationWarning(null);
    candidatePad.current?.clear();
    parentPad.current?.clear();
    setCandidateSigDataUrl(null);
    setParentSigDataUrl(null);
    setErrors({});
    setForm(prev => ({ ...prev, sitting: '', previousRegistrationNumber: '', parentGuardianName: '' }));
  };

  const textField = (
    key: keyof CxcFormState,
    label: string,
    opts: { required?: boolean; placeholder?: string; type?: string; uppercase?: boolean; hint?: string; span?: number } = {},
  ) => (
    <View style={[styles.field, opts.span === 2 && styles.span2, opts.span === 3 && styles.span3]}>
      <Text style={styles.label}>{label}{opts.required ? <Text style={styles.required}> *</Text> : null}</Text>
      <WebInput
        type={opts.type ?? 'text'}
        value={form[key]}
        placeholder={opts.placeholder}
        onChange={(e: any) => setField(key, e.target.value)}
        style={{ ...webFieldStyle(colors, !!errors[key]), textTransform: opts.uppercase === false ? 'none' : 'uppercase' }}
      />
      {opts.hint ? <Text style={styles.hint}>{opts.hint}</Text> : null}
      {errors[key] ? <Text style={styles.errorText}>{errors[key]}</Text> : null}
    </View>
  );

  const chip = (selected: boolean, label: string, onPress: () => void, disabled = false) => (
    <TouchableOpacity style={[styles.chip, selected && styles.chipActive, disabled && styles.chipDisabled]} onPress={onPress} disabled={disabled}>
      <Text style={[styles.chipText, selected && styles.chipTextActive]}>{label}</Text>
    </TouchableOpacity>
  );

  if (!cxcOptions) {
    return (
      <WebPageShell title="CXC / CSEC Registration">
        <View style={styles.card}>
          {cxcOptionsError
            ? <Text style={styles.hint}>The CXC registration details could not be loaded. Please try again later.</Text>
            : <LoadingSpinner message="Loading CXC registration..." />}
        </View>
      </WebPageShell>
    );
  }

  if (!selectedStudent) {
    return (
      <WebPageShell title="CXC / CSEC Registration">
        <View style={styles.card}><Text style={styles.hint}>Select a child to register them for CXC / CSEC examinations.</Text></View>
      </WebPageShell>
    );
  }

  if (!isStudentEligibleForCxc(selectedStudent)) {
    return (
      <WebPageShell title="CXC / CSEC Registration" maxWidth={800}>
        <TouchableOpacity style={styles.backRow} onPress={() => navigation.goBack()} accessibilityLabel="Back">
          <FeatherIcon name="arrow-left" size={16} color={colors.primary} />
          <Text style={styles.backRowText}>Back to Dashboard</Text>
        </TouchableOpacity>
        <View style={[styles.card, { alignItems: 'center', paddingVertical: Spacing.xl, marginTop: Spacing.md }]}>
          <View style={[styles.cardIconBox, { width: 52, height: 52, borderRadius: 26, marginBottom: Spacing.md }]}>
            <FeatherIcon name="info" size={24} color={colors.primary} />
          </View>
          <Text style={[styles.cardTitle, { textAlign: 'center', marginBottom: Spacing.xs }]}>
            Secondary Class Required
          </Text>
          <Text style={[styles.hint, { textAlign: 'center', maxWidth: 480, marginBottom: Spacing.lg }]}>
            CXC / CSEC examination registration is only available for secondary school students in Year 7 and above. {selectedStudent.name} is currently enrolled in {selectedStudent.class || 'a primary class'}.
          </Text>
          <TouchableOpacity style={styles.primaryBtn} onPress={() => navigation.goBack()}>
            <Text style={styles.primaryBtnText}>Back to Dashboard</Text>
          </TouchableOpacity>
        </View>
      </WebPageShell>
    );
  }

  return (
    <WebPageShell title="CXC / CSEC Registration" maxWidth={1060}>
      {/* Breadcrumb Back Row */}
      <TouchableOpacity style={styles.backRow} onPress={() => navigation.goBack()} accessibilityLabel="Back">
        <FeatherIcon name="arrow-left" size={16} color={colors.primary} />
        <Text style={styles.backRowText}>Back to Dashboard</Text>
      </TouchableOpacity>

      {/* Senior-Level Enterprise Hero Banner with Official Highlights and Flyer Showcase */}
      <View style={styles.heroBanner}>
        {/* Left Column: Accreditation, Title, Candidate Info & Highlight Badges */}
        <View style={styles.heroLeftCol}>
          <View style={styles.heroAccreditationPill}>
            <FeatherIcon name="award" size={13} color="#FDE68A" />
            <Text style={styles.heroAccreditationText}>
              {CXC_CENTRE.name} • Centre No. {CXC_CENTRE.number}
            </Text>
          </View>

          <Text style={styles.heroTitle}>CXC / CSEC Examination Registration</Text>
          <Text style={styles.heroSubtitle}>
            Official Caribbean Examinations Council (CSEC &amp; CAPE) entry registration for{' '}
            <Text style={{ fontWeight: '700', color: colors.textInverse }}>{name || selectedStudent.name}</Text>
            {selectedStudent.class ? ` • Grade ${selectedStudent.class}` : ''}
          </Text>

          {/* Header Highlights Bar */}
          <View style={styles.highlightsContainer}>
            {sittingsWithStatus.map(s => (
              <View
                key={s.id}
                style={[
                  styles.highlightCard,
                  !s.isOpen && styles.highlightCardClosed,
                ]}
              >
                <View
                  style={[
                    styles.highlightIconBox,
                    !s.isOpen && styles.highlightIconBoxClosed,
                  ]}
                >
                  <FeatherIcon
                    name={s.isOpen ? 'calendar' : 'x-circle'}
                    size={15}
                    color={s.isOpen ? (s.key === 'January' ? '#FDE68A' : '#93C5FD') : '#FCA5A5'}
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.highlightLabel, !s.isOpen && styles.highlightLabelClosed]}>
                    {s.label} Sitting
                  </Text>
                  <Text style={[styles.highlightValue, !s.isOpen && styles.highlightValueClosed]}>
                    {s.isOpen ? `Closes ${s.deadline}` : `Closed on ${s.deadline}`}
                  </Text>
                </View>
                <View
                  style={[
                    styles.highlightBadge,
                    !s.isOpen && styles.highlightBadgeClosed,
                  ]}
                >
                  <Text
                    style={[
                      styles.highlightBadgeText,
                      !s.isOpen && styles.highlightBadgeTextClosed,
                    ]}
                  >
                    {s.isOpen ? 'Open' : 'Closed'}
                  </Text>
                </View>
              </View>
            ))}

            <View style={[styles.highlightCard, styles.highlightWarningCard]}>
              <View style={styles.highlightIconBox}>
                <FeatherIcon name="alert-triangle" size={15} color="#FDE68A" />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.highlightLabel}>Late Submission Notice</Text>
                <Text style={styles.highlightValue}>
                  Late submissions incur a {formatMoney(CXC_FEES.late)} KYD penalty fee.
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* Right Column: Official Circular Flyer Showcase */}
        <View style={styles.heroRightCol}>
          <TouchableOpacity
            style={styles.flyerCard}
            onPress={() => setShowPosterModal(true)}
            activeOpacity={0.88}
            accessibilityLabel="View official CXC announcement flyer"
          >
            <View style={styles.flyerBadgeOverlay}>
              <View style={styles.pulseDot} />
              <Text style={styles.flyerBadgeText}>Official Notice</Text>
            </View>

            <Image
              source={BANNER_IMAGE}
              style={styles.flyerImage as any}
              resizeMode="cover"
            />

            <View style={styles.flyerFooter}>
              <View style={styles.flyerFooterLeft}>
                <FeatherIcon name="file-text" size={12} color="#FDE68A" />
                <Text style={styles.flyerFooterNoticeText}>Centre 060103 Notice</Text>
              </View>
              <View style={styles.flyerFooterRight}>
                <Text style={styles.flyerExpandText}>Full View</Text>
                <FeatherIcon name="maximize-2" size={12} color="#FFFFFF" />
              </View>
            </View>
          </TouchableOpacity>
        </View>
      </View>

      {/* Tabs Row */}
      <View style={styles.tabs}>
        {(['form', 'receipts'] as TabType[]).map(t => (
          <TouchableOpacity key={t} style={[styles.tab, tab === t && styles.tabActive]} onPress={() => setTab(t)}>
            <Text style={[styles.tabText, tab === t && styles.tabTextActive]}>
              {t === 'form' ? 'New registration' : `Previous registrations (${registrations.length})`}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {tab === 'receipts' && (
        <View style={styles.card}>
          <View style={styles.cardHeaderWithIcon}>
            <View style={styles.cardIconBox}>
              <FeatherIcon name="file-text" size={18} color={colors.primary} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.cardTitle}>Previous registrations</Text>
              <Text style={styles.cardSubtitle}>
                Bring the invoice number, student identification and payment voucher to the school finance counter to complete entry.
              </Text>
            </View>
          </View>

          {registrationsLoading ? <LoadingSpinner size="small" message="Loading your registrations..." /> : null}
          {!registrationsLoading && registrations.length === 0 ? <Text style={styles.hint}>No CXC registrations yet.</Text> : null}
          {registrations.map(r => (
            <TouchableOpacity
              key={r.id}
              style={styles.receiptRow}
              onPress={() => setViewing(r)}
              activeOpacity={0.7}
              accessibilityRole="button"
              accessibilityLabel={`View registration ${r.invoiceNumber}`}
            >
              <Text style={[styles.receiptCell, styles.receiptInvoice, { flex: 1.4 }]}>{r.invoiceNumber}</Text>
              <Text style={[styles.receiptCell, { flex: 1 }]}>{r.sittingMonth === 'MayJune' ? 'May/June' : r.sittingMonth} {r.sittingYear}</Text>
              <Text style={[styles.receiptCell, { flex: 0.8 }]}>{r.subjects.length} subject{r.subjects.length === 1 ? '' : 's'}</Text>
              <Text style={[styles.receiptCell, { flex: 1 }]}>{formatMoney(r.estimatedTotalCost)} KYD</Text>
              <Text style={[styles.statusPill]}>{cxcStatusLabel(r)}</Text>
            </TouchableOpacity>
          ))}
        </View>
      )}

      {tab === 'form' && receipt && (
        <View style={[styles.card, { alignItems: 'center' }]}>
          <View style={styles.successIcon}><FeatherIcon name="check" size={30} color={colors.success} /></View>
          <Text style={styles.successTitle}>Registration received</Text>
          <Text style={styles.hint}>A confirmation has been emailed to {form.email.trim()}.</Text>
          <View style={styles.invoiceBox}>
            <Text style={styles.invoiceLabel}>INVOICE NUMBER</Text>
            <Text style={styles.invoiceNumber}>{receipt.invoiceNumber}</Text>
          </View>
          <Text style={[styles.hint, { textAlign: 'center', maxWidth: 560 }]}>
            Bring this invoice number, proof of identification and payment of {formatMoney(receipt.estimatedTotalCost)} KYD to the school office. The registration is not finalised with CXC until payment is received.
          </Text>
          <View style={[styles.invoiceBox, { maxWidth: 560 }]}>
            <Text style={styles.invoiceLabel}>PRACTICAL &amp; LATE FEE CONDITIONS</Text>
            <Text style={styles.hint}>
              Practical Fee ({formatMoney(CXC_FEES.practical)} per practical) is only charged for subjects that require a practical exam, once the office reviews your subjects.
            </Text>
            <Text style={[styles.hint, { marginTop: Spacing.xs }]}>
              Late Fee ({formatMoney(CXC_FEES.late)}) is only charged if this registration is submitted after the CXC deadline.
            </Text>
            <Text style={[styles.hint, { marginTop: Spacing.xs }]}>
              Neither fee is included in the amount above unless it applies to you.
            </Text>
          </View>
          <View style={{ flexDirection: 'row', gap: Spacing.sm, marginTop: Spacing.md }}>
            <TouchableOpacity style={styles.secondaryBtn} onPress={handlePrint}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <FeatherIcon name="printer" size={15} color={colors.primary} />
                <Text style={styles.secondaryBtnText}>Print Registration Form</Text>
              </View>
            </TouchableOpacity>
            <TouchableOpacity style={styles.secondaryBtn} onPress={() => setTab('receipts')}><Text style={styles.secondaryBtnText}>View registrations</Text></TouchableOpacity>
            <TouchableOpacity style={styles.primaryBtn} onPress={startAnother}><Text style={styles.primaryBtnText}>Register another sitting</Text></TouchableOpacity>
          </View>
          {/* Off-screen copy: what "Print Registration Form" above prints. */}
          <View style={styles.hiddenPrintHost}>{renderPrintDocument(successDocRef)}</View>
        </View>
      )}

      {tab === 'form' && !receipt && allSittingsClosed && (
        <View style={styles.closedWindowCard}>
          <View style={styles.closedWindowHeader}>
            <View style={styles.closedWindowIconBox}>
              <FeatherIcon name="lock" size={26} color="#DC2626" />
            </View>
            <View style={{ flex: 1 }}>
              <View style={styles.closedHeaderRow}>
                <Text style={styles.closedWindowTitle}>Official CXC Registration Window Closed</Text>
                <View style={styles.closedBadge}>
                  <Text style={styles.closedBadgeText}>CLOSED FOR 2027</Text>
                </View>
              </View>
              <Text style={styles.closedWindowSubtitle}>
                The Caribbean Examinations Council registration deadline for all 2027 examination sittings has passed. Online submissions are now closed.
              </Text>
            </View>
          </View>

          <View style={styles.closedDeadlinesSummaryBox}>
            <Text style={styles.closedSummaryHeading}>Official Sitting Cutoffs (Centre 060103):</Text>
            {sittingsWithStatus.map(s => (
              <View key={s.id} style={styles.closedDeadlineSummaryRow}>
                <FeatherIcon name="x-circle" size={14} color="#EF4444" />
                <Text style={styles.closedDeadlineSummaryText}>
                  <Text style={{ fontWeight: '700' }}>{s.label}:</Text> Registration officially closed on <Text style={{ fontWeight: '700' }}>{s.deadline}</Text>.
                </Text>
              </View>
            ))}
          </View>

          <View style={styles.closedOfficeNoticeBox}>
            <View style={styles.closedOfficeHeader}>
              <FeatherIcon name="help-circle" size={16} color={colors.primary} />
              <Text style={styles.closedOfficeTitle}>Late Inquiries &amp; Emergency Considerations</Text>
            </View>
            <Text style={styles.closedOfficeBody}>
              Parents seeking special administrative dispensation or late candidate additions must consult the Cayman Academy administration office in person. If accepted under CXC regulations, late registrations require payment of the standard {formatMoney(CXC_FEES.late)} KYD Late Penalty Fee assessed by the school cashier.
            </Text>
          </View>

          <View style={styles.closedActionsRow}>
            <TouchableOpacity
              style={styles.primaryBtn}
              onPress={() => setTab('receipts')}
              activeOpacity={0.85}
            >
              <Text style={styles.primaryBtnText}>
                View Existing Registrations ({registrations.length})
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {tab === 'form' && !receipt && !allSittingsClosed && (
        <>
          {/* Section 1: Examination Sitting */}
          <View style={styles.card}>
            <View style={styles.cardHeaderWithIcon}>
              <View style={styles.cardIconBox}>
                <FeatherIcon name="calendar" size={18} color={colors.primary} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.cardTitle}>Examination sitting <Text style={styles.required}>*</Text></Text>
                <Text style={styles.cardSubtitle}>Select your target examination session. Registration deadlines are mandated by CXC.</Text>
              </View>
            </View>

            {hasAnyClosedSitting && (
              <View style={styles.partialDeadlineNotice}>
                <FeatherIcon name="alert-circle" size={18} color="#D97706" />
                <View style={{ flex: 1 }}>
                  <Text style={styles.partialDeadlineNoticeTitle}>Session Deadline Notice</Text>
                  <Text style={styles.partialDeadlineNoticeText}>
                    Registration for{' '}
                    {sittingsWithStatus.filter(s => !s.isOpen).map(s => s.label).join(', ')}{' '}
                    has closed. Only open examination sessions can be selected below.
                  </Text>
                </View>
              </View>
            )}

            <View style={styles.row}>
              {sittingsWithStatus.map(s => {
                const isSelected = form.sitting === s.id;
                const isClosed = !s.isOpen;
                return (
                  <TouchableOpacity
                    key={s.id}
                    style={[
                      styles.sittingCard,
                      isSelected && styles.sittingCardActive,
                      isClosed && styles.sittingCardClosed,
                    ]}
                    onPress={() => {
                      if (isClosed) {
                        showToast(
                          `Registration for ${s.label} closed on ${s.deadline}. Online registration is closed for this session.`,
                          'error',
                        );
                        return;
                      }
                      setField('sitting', s.id);
                    }}
                    activeOpacity={isClosed ? 0.75 : 0.85}
                    accessibilityLabel={`${s.label} - ${isClosed ? 'Closed' : 'Open'}`}
                  >
                    <FeatherIcon
                      name={isSelected ? 'check-circle' : isClosed ? 'slash' : 'circle'}
                      size={20}
                      color={isSelected ? colors.primary : isClosed ? '#DC2626' : colors.textMuted}
                    />
                    <View style={{ marginLeft: Spacing.sm, flex: 1 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                        <Text style={[styles.sittingLabel, isClosed && styles.sittingLabelClosed]}>
                          {s.label}
                        </Text>
                        {isClosed && (
                          <View style={styles.closedPillBadge}>
                            <Text style={styles.closedPillBadgeText}>CLOSED</Text>
                          </View>
                        )}
                      </View>
                      <Text style={[styles.hint, isClosed && styles.hintClosed]}>
                        {isClosed ? `Deadline passed: ${s.deadline}` : `Deadline: ${s.deadline}`}
                      </Text>
                    </View>
                    <View style={[
                      styles.sittingStatusTag,
                      isSelected && styles.sittingStatusTagActive,
                      isClosed && styles.sittingStatusTagClosed,
                    ]}>
                      <Text style={[
                        styles.sittingStatusTagText,
                        isSelected && styles.sittingStatusTagTextActive,
                        isClosed && styles.sittingStatusTagTextClosed,
                      ]}>
                        {isSelected ? 'Selected' : isClosed ? 'Closed' : 'Open'}
                      </Text>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
            {errors.sitting ? <Text style={styles.errorText}>{errors.sitting}</Text> : null}
            <WebAnchor
              href={CXC_SBA_FAQ_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: colors.primary,
                fontSize: 13,
                fontWeight: '600',
                marginTop: 12,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              Re-sit (SBA reuse) guidelines &amp; CXC reformed SBA framework FAQs →
            </WebAnchor>
          </View>

          {/* Section 2: Candidate Details */}
          <View style={styles.card}>
            <View style={styles.cardHeaderWithIcon}>
              <View style={styles.cardIconBox}>
                <FeatherIcon name="user" size={18} color={colors.primary} />
              </View>
              <View style={{ flex: 1 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <Text style={styles.cardTitle}>Candidate details</Text>
                  <View style={styles.prefilledBadge}>
                    <FeatherIcon name="check-circle" size={11} color={colors.success} />
                    <Text style={styles.prefilledBadgeText}>Prefilled from school records</Text>
                  </View>
                </View>
                <Text style={styles.cardSubtitle}>
                  Names must match the candidate&apos;s official passport or birth certificate exactly. CXC charges an amendment fee for variations.
                </Text>
              </View>
            </View>

            <View style={styles.grid}>
              {textField('firstName', 'First name', { required: true })}
              {textField('middleName', 'Middle name')}
              {textField('lastName', 'Last name', { required: true })}
              {textField('dateOfBirth', 'Date of birth', { required: true, type: 'date', uppercase: false })}
              <View style={styles.field}>
                <Text style={styles.label}>Gender <Text style={styles.required}> *</Text></Text>
                <View style={styles.chipRow}>
                  {chip(form.gender === 'Female', 'Female', () => setField('gender', 'Female'))}
                  {chip(form.gender === 'Male', 'Male', () => setField('gender', 'Male'))}
                </View>
                {errors.gender ? <Text style={styles.errorText}>{errors.gender}</Text> : null}
              </View>
              {textField('gradeOrClass', 'Grade')}
              {textField('previousRegistrationNumber', 'Previous registration no. (if re-sit)', {
                placeholder: 'e.g. 060103XXXX',
                hint: 'Leave blank if this is the candidate’s first CXC sitting.',
                span: 2,
              })}
            </View>
          </View>

          {/* Section 3: Contact Details */}
          <View style={styles.card}>
            <View style={styles.cardHeaderWithIcon}>
              <View style={styles.cardIconBox}>
                <FeatherIcon name="phone" size={18} color={colors.primary} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.cardTitle}>Contact &amp; Residence details</Text>
                <Text style={styles.cardSubtitle}>Official confirmation, candidate timetable, and invoice number will be dispatched to this email.</Text>
              </View>
            </View>

            <View style={styles.grid}>
              {textField('address', 'Residential address', { required: true, span: 3 })}
              {textField('cellPhone', 'Cell Phone', { required: true, type: 'tel', uppercase: false })}
              {textField('homePhone', 'Telephone (home)', { type: 'tel', uppercase: false })}
              {textField('email', 'Email Address', { required: true, type: 'email', uppercase: false })}
            </View>
          </View>

          {/* Section 4: Photo Identification (Senior & Enterprise Studio) */}
          <View style={styles.card}>
            <View style={styles.cardHeaderWithIcon}>
              <View style={styles.cardIconBox}>
                <FeatherIcon name="camera" size={18} color={colors.primary} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.cardTitle}>
                  Candidate Photo Identification <Text style={styles.required}>*</Text>
                </Text>
                <Text style={styles.cardSubtitle}>
                  Official passport-standard portrait required by CXC for student identity verification, candidate timetable, and exam hall admission.
                </Text>
              </View>
            </View>

            <View style={styles.photoStudioContainer}>
              {/* Left: The Official Passport Photo Frame */}
              <View style={styles.photoFrameWrapper}>
                <View style={[styles.photoFrame, !!photoDataUrl && styles.photoFrameActive]}>
                  {photoDataUrl ? (
                    <>
                      <WebInput
                        type="image"
                        src={photoDataUrl}
                        alt="Candidate Photo"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
                      />
                      <View style={styles.photoFrameTagVerified}>
                        <FeatherIcon name="check-circle" size={11} color="#FFFFFF" />
                        <Text style={styles.photoFrameTagText}>Verified Photo</Text>
                      </View>
                    </>
                  ) : (
                    <View style={styles.photoEmptyPlaceholder}>
                      <View style={styles.photoGuideSilhouette}>
                        <FeatherIcon name="user" size={40} color={colors.textMuted} />
                      </View>
                      <Text style={styles.photoGuideText}>Passport Size</Text>
                      <Text style={styles.photoGuideSubtext}>35 × 45 mm</Text>
                      <View style={styles.photoFrameTagRequired}>
                        <Text style={styles.photoFrameTagRequiredText}>Photo Required</Text>
                      </View>
                    </View>
                  )}
                </View>
              </View>

              {/* Right: Actions & CXC Official Guidance Checklist */}
              <View style={styles.photoRightCol}>
                <View style={styles.photoActionButtonsRow}>
                  <TouchableOpacity style={styles.primaryPhotoBtn} onPress={handleChoosePhotoClick}>
                    <FeatherIcon name="upload-cloud" size={15} color={colors.textInverse} />
                    <Text style={styles.primaryPhotoBtnText}>
                      {photoDataUrl ? 'Replace Photo' : 'Upload Passport Photo'}
                    </Text>
                  </TouchableOpacity>

                  {displayPhoto && displayPhoto !== photoDataUrl ? (
                    <TouchableOpacity
                      style={styles.schoolPhotoBtn}
                      onPress={() => {
                        setPhotoDataUrl(displayPhoto);
                        if (errors.photo) {
                          setErrors(prev => {
                            const next = { ...prev };
                            delete next.photo;
                            return next;
                          });
                        }
                        showToast('Loaded approved student portrait from school records.');
                      }}
                    >
                      <FeatherIcon name="user-check" size={15} color={colors.primary} />
                      <Text style={styles.schoolPhotoBtnText}>Use School Photo</Text>
                    </TouchableOpacity>
                  ) : null}

                  {photoDataUrl ? (
                    <TouchableOpacity
                      style={styles.removePhotoBtn}
                      onPress={() => setPhotoDataUrl(null)}
                    >
                      <FeatherIcon name="trash-2" size={14} color={colors.danger} />
                      <Text style={styles.removePhotoBtnText}>Remove</Text>
                    </TouchableOpacity>
                  ) : null}
                </View>

                {errors.photo ? (
                  <View style={styles.photoErrorPill}>
                    <FeatherIcon name="alert-circle" size={14} color={colors.danger} />
                    <Text style={styles.photoErrorPillText}>{errors.photo}</Text>
                  </View>
                ) : null}

                {/* CXC Standard Photo Checklist Card */}
                <View style={styles.photoChecklistCard}>
                  <View style={styles.photoChecklistHeader}>
                    <FeatherIcon name="shield" size={14} color={colors.primary} />
                    <Text style={styles.photoChecklistTitle}>Official CXC Passport Photo Standards</Text>
                  </View>
                  <View style={styles.photoChecklistItems}>
                    <View style={styles.photoChecklistItem}>
                      <FeatherIcon name="check" size={13} color={colors.success} />
                      <Text style={styles.photoChecklistText}>Clear, front-facing portrait with entire face and ears visible</Text>
                    </View>
                    <View style={styles.photoChecklistItem}>
                      <FeatherIcon name="check" size={13} color={colors.success} />
                      <Text style={styles.photoChecklistText}>Plain light background (white, off-white or light blue) without shadows</Text>
                    </View>
                    <View style={styles.photoChecklistItem}>
                      <FeatherIcon name="check" size={13} color={colors.success} />
                      <Text style={styles.photoChecklistText}>Neutral facial expression, eyes open and looking straight at camera</Text>
                    </View>
                    <View style={styles.photoChecklistItem}>
                      <FeatherIcon name="check" size={13} color={colors.success} />
                      <Text style={styles.photoChecklistText}>Accepted file formats: JPG or PNG • Maximum file size: 5 MB</Text>
                    </View>
                  </View>
                </View>
              </View>
            </View>
          </View>

          {/* Section 4b: Identification document - choose the document, enter its details, upload it (image or PDF). Separate from the portrait above. */}
          <View style={styles.card}>
            <View style={styles.cardHeaderWithIcon}>
              <View style={styles.cardIconBox}>
                <FeatherIcon name="file-text" size={18} color={colors.primary} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.cardTitle}>
                  Identification Document <Text style={styles.required}>*</Text>
                </Text>
                <Text style={styles.cardSubtitle}>
                  Please upload a clear image of your full National ID, Driving License, or Passport information page. Do not upload only a passport-size photograph.
                </Text>
              </View>
            </View>

            <View style={[styles.field, styles.span3, { marginBottom: Spacing.md }]}>
              <Text style={styles.label}>Document type <Text style={styles.required}>*</Text></Text>
              <View style={styles.chipRow}>
                {IDENTIFICATION_TYPES.map(t => (
                  <React.Fragment key={t.value}>
                    {chip(identification.type === t.value, t.label, () => setIdentificationDetail({ type: t.value, number: sanitizeIdentificationNumber(t.value, identification.number), country: t.value === 'PASSPORT' ? identification.country : '' }))}
                  </React.Fragment>
                ))}
              </View>
              {errors.identificationType ? <Text style={styles.errorText}>{errors.identificationType}</Text> : null}
            </View>

            {identification.type ? (
              <View style={styles.grid}>
                <View style={styles.field}>
                  <Text style={styles.label}>{IDENTIFICATION_TYPES.find(t => t.value === identification.type)?.numberLabel} <Text style={styles.required}>*</Text></Text>
                  <WebInput
                    type="text"
                    value={identification.number}
                    maxLength={50}
                    onChange={(e: any) => setIdentificationDetail({ number: sanitizeIdentificationNumber(identification.type, e.target.value) })}
                    style={{ ...webFieldStyle(colors, !!errors.identificationNumber), textTransform: 'uppercase' }}
                  />
                  {errors.identificationNumber ? <Text style={styles.errorText}>{errors.identificationNumber}</Text> : null}
                </View>

                {identification.type === 'PASSPORT' ? (
                  <View style={styles.field}>
                    <Text style={styles.label}>Country of issue <Text style={styles.required}>*</Text></Text>
                    <WebInput
                      type="text"
                      value={identification.country}
                      maxLength={100}
                      onChange={(e: any) => setIdentificationDetail({ country: sanitizeCountry(e.target.value) })}
                      style={webFieldStyle(colors, !!errors.identificationCountry)}
                    />
                    {errors.identificationCountry ? <Text style={styles.errorText}>{errors.identificationCountry}</Text> : null}
                  </View>
                ) : null}

                <View style={styles.field}>
                  <Text style={styles.label}>Expiry date (optional)</Text>
                  <WebInput
                    type="date"
                    min={todayIsoDate()}
                    value={identification.expiry}
                    onChange={(e: any) => setIdentificationDetail({ expiry: e.target.value })}
                    style={webFieldStyle(colors, !!errors.identificationExpiry)}
                  />
                  {errors.identificationExpiry ? <Text style={styles.errorText}>{errors.identificationExpiry}</Text> : null}
                </View>
              </View>
            ) : (
              <Text style={styles.hint}>Choose the document you will upload to enter its details.</Text>
            )}

            <View style={styles.photoStudioContainer}>
              <View style={styles.idDocumentFrameWrapper}>
                <View style={[styles.idDocumentFrame, !!identificationDataUrl && styles.photoFrameActive]}>
                  {identificationDataUrl ? (
                    // A PDF and an image are shown the same way: an icon, the file name, its kind and its size.
                    <View style={styles.idPdfTile}>
                      <FeatherIcon
                        name={identificationDataUrl.startsWith(IDENTIFICATION_PDF_PREFIX) ? 'file-text' : 'image'}
                        size={36}
                        color={colors.primary}
                      />
                      <View style={styles.idPdfNameRow}>
                        <Text style={[styles.idPdfName, styles.idPdfNameBase]} numberOfLines={1}>{splitFileName(identificationFileName ?? 'Identification document')[0]}</Text>
                        <Text style={styles.idPdfName}>{splitFileName(identificationFileName ?? 'Identification document')[1]}</Text>
                      </View>
                      <View style={styles.idPdfMetaRow}>
                        <View style={styles.idPdfBadge}><Text style={styles.idPdfBadgeText}>{identificationKind(identificationDataUrl)}</Text></View>
                        <Text style={styles.idPdfMeta}>{dataUrlSizeLabel(identificationDataUrl)}</Text>
                      </View>
                    </View>
                  ) : (
                    <View style={styles.photoEmptyPlaceholder}>
                      <FeatherIcon name="file-text" size={36} color={colors.textMuted} />
                      <Text style={styles.photoGuideText}>Full Document</Text>
                      <Text style={styles.photoGuideSubtext}>Image or PDF</Text>
                    </View>
                  )}
                </View>
              </View>

              <View style={styles.photoRightCol}>
                <View style={styles.photoActionButtonsRow}>
                  <TouchableOpacity style={styles.primaryPhotoBtn} onPress={handleChooseIdentificationClick}>
                    <FeatherIcon name="upload-cloud" size={15} color={colors.textInverse} />
                    <Text style={styles.primaryPhotoBtnText}>
                      {identificationDataUrl ? 'Replace File' : 'Upload File'}
                    </Text>
                  </TouchableOpacity>

                  {identificationDataUrl ? (
                    <TouchableOpacity
                      style={styles.removePhotoBtn}
                      onPress={() => { setIdentificationDataUrl(null); setIdentificationFileName(null); setIdentificationWarning(null); }}
                    >
                      <FeatherIcon name="trash-2" size={14} color={colors.danger} />
                      <Text style={styles.removePhotoBtnText}>Remove</Text>
                    </TouchableOpacity>
                  ) : null}
                </View>

                {identificationDataUrl && identificationFileName ? (
                  <Text style={[styles.hint, { marginBottom: Spacing.sm }]}>Selected file: {identificationFileName}</Text>
                ) : null}

                {errors.identificationDocument ? (
                  <View style={styles.photoErrorPill}>
                    <FeatherIcon name="alert-circle" size={14} color={colors.danger} />
                    <Text style={styles.photoErrorPillText}>{errors.identificationDocument}</Text>
                  </View>
                ) : null}

                {identificationWarning ? (
                  <View style={styles.idDocumentWarnPill}>
                    <FeatherIcon name="alert-triangle" size={14} color={colors.warning} />
                    <Text style={styles.idDocumentWarnPillText}>{identificationWarning}</Text>
                  </View>
                ) : null}

                <View style={styles.photoChecklistCard}>
                  <View style={styles.photoChecklistHeader}>
                    <FeatherIcon name="shield" size={14} color={colors.primary} />
                    <Text style={styles.photoChecklistTitle}>Identification Document Requirements</Text>
                  </View>
                  <View style={styles.photoChecklistItems}>
                    <View style={styles.photoChecklistItem}>
                      <FeatherIcon name="check" size={13} color={colors.success} />
                      <Text style={styles.photoChecklistText}>Your full National ID, Driving License, or the full Passport information page</Text>
                    </View>
                    <View style={styles.photoChecklistItem}>
                      <FeatherIcon name="check" size={13} color={colors.success} />
                      <Text style={styles.photoChecklistText}>The whole document, with all details clearly readable</Text>
                    </View>
                    <View style={styles.photoChecklistItem}>
                      <FeatherIcon name="check" size={13} color={colors.success} />
                      <Text style={styles.photoChecklistText}>Do not upload only a passport-size photograph</Text>
                    </View>
                    <View style={styles.photoChecklistItem}>
                      <FeatherIcon name="check" size={13} color={colors.success} />
                      <Text style={styles.photoChecklistText}>Accepted file formats: PDF, JPG or PNG • Maximum file size: 5 MB</Text>
                    </View>
                  </View>
                </View>
              </View>
            </View>
          </View>

          {/* Section 5: Subjects */}
          <View style={styles.card}>
            <View style={styles.cardHeaderWithIcon}>
              <View style={styles.cardIconBox}>
                <FeatherIcon name="book-open" size={18} color={colors.primary} />
              </View>
              <View style={{ flex: 1 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                  <Text style={styles.cardTitle}>Subjects ({selectedSubjects.length} selected)</Text>
                  <View style={styles.subjectCountBadge}>
                    <Text style={styles.subjectCountBadgeText}>{selectedSubjects.length} chosen</Text>
                  </View>
                </View>
                <Text style={styles.cardSubtitle}>
                  Tick Yes for each subject. Tick Re-sit only if reusing an existing School-Based Assessment (minimum 60% SBA grade).
                </Text>
              </View>
            </View>

            <View style={[styles.row, { alignItems: 'center', marginVertical: Spacing.xs }]}>
              <TextInput
                style={[styles.searchInput]}
                value={subjectSearch}
                onChangeText={setSubjectSearch}
                placeholder="Search subjects by name..."
                placeholderTextColor={colors.textMuted}
              />
              {chip(subjectFilter === 'ALL', `All (${subjects.length})`, () => setSubjectFilter('ALL'))}
              {chip(subjectFilter === 'GENERAL', 'CSEC General', () => setSubjectFilter('GENERAL'))}
              {chip(subjectFilter === 'CAPE', 'CAPE', () => setSubjectFilter('CAPE'))}
              {chip(subjectFilter === 'SELECTED', `Selected (${selectedSubjects.length})`, () => setSubjectFilter('SELECTED'))}
            </View>

            <View style={[styles.subjectRow, styles.subjectHead]}>
              <Text style={[styles.subjectHeadText, { flex: 1 }]}>SUBJECT NAME</Text>
              <Text style={[styles.subjectHeadText, { width: 130 }]}>PROFICIENCY</Text>
              <Text style={[styles.subjectHeadText, { width: 75, textAlign: 'center' }]}>ENROLL</Text>
              <Text style={[styles.subjectHeadText, { width: 85, textAlign: 'center' }]}>RE-SIT</Text>
            </View>

            {subjectsLoading ? <LoadingSpinner size="small" message="Loading subjects..." /> : null}
            {visibleSubjects.map(s => {
              const sel = selection[s.name];
              return (
                <View key={s.id} style={[styles.subjectRow, sel?.selected && styles.subjectRowSelected]}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.subjectName}>{s.name}</Text>
                    {s.sbaRequiredElsewhere ? (
                      <View style={styles.sbaWarningTag}>
                        <FeatherIcon name="alert-triangle" size={11} color={colors.warning} />
                        <Text style={styles.sbaWarningTagText}>External SBA transfer required</Text>
                      </View>
                    ) : null}
                  </View>
                  <Text style={[styles.proficiencyTag, s.proficiency === 'CAPE' && styles.proficiencyCape, { width: 130 }]}>
                    {s.proficiency === 'CAPE' ? 'CAPE (Unit)' : 'CSEC General'}
                  </Text>
                  <TouchableOpacity
                    style={{ width: 75, alignItems: 'center' }}
                    onPress={() => onToggleSubject(s.name, 'selected')}
                    accessibilityLabel={`Select ${s.name}`}
                  >
                    <FeatherIcon
                      name={sel?.selected ? 'check-square' : 'square'}
                      size={20}
                      color={sel?.selected ? colors.primary : colors.textMuted}
                    />
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={{ width: 85, alignItems: 'center', opacity: sel?.selected ? 1 : 0.4 }}
                    disabled={!sel?.selected}
                    onPress={() => onToggleSubject(s.name, 'resit')}
                    accessibilityLabel={`Mark ${s.name} as resit`}
                  >
                    <FeatherIcon
                      name={sel?.resit ? 'check-square' : 'square'}
                      size={20}
                      color={sel?.resit ? colors.warning : colors.textMuted}
                    />
                  </TouchableOpacity>
                </View>
              );
            })}
            {!subjectsLoading && visibleSubjects.length === 0 ? <Text style={styles.hint}>No subjects match your query.</Text> : null}
            {errors.subjects ? <Text style={styles.errorText}>{errors.subjects}</Text> : null}
          </View>

          {/* Section 6: Fee Structure */}
          <View style={styles.card}>
            <View style={styles.cardHeaderWithIcon}>
              <View style={styles.cardIconBox}>
                <FeatherIcon name="dollar-sign" size={18} color={colors.primary} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.cardTitle}>Fee structure &amp; assessment</Text>
                <Text style={styles.cardSubtitle}>
                  All fees are payable at Cayman Academy office counter (Debit card or FirstCaribbean International Bank transfer).
                </Text>
              </View>
            </View>

            {invoiceLines.map(line => (
              <View key={line.code} style={styles.feeRow}>
                <Text style={[styles.feeLabel, line.quantity === 0 && styles.feeMuted]}>{line.description}</Text>
                <Text style={[styles.feeAmount, line.quantity === 0 && styles.feeMuted]}>
                  {line.quantity === 0 ? '—' : `${formatMoney(line.amount)} KYD`}
                </Text>
              </View>
            ))}

            <View style={[styles.feeRow, styles.feeTotalRow]}>
              <View>
                <Text style={styles.feeTotalLabel}>Estimated Total Due</Text>
                <Text style={styles.feeTotalSubtitle}>Final verification and receipt stamped upon office settlement</Text>
              </View>
              <Text style={styles.feeTotalAmount}>{formatMoney(estimatedTotal)} KYD</Text>
            </View>
          </View>

          {/* Section 7: Declarations & Signatures */}
          <View style={styles.card}>
            <View style={styles.cardHeaderWithIcon}>
              <View style={styles.cardIconBox}>
                <FeatherIcon name="edit-3" size={18} color={colors.primary} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.cardTitle}>Declarations &amp; Digital Signatures</Text>
                <Text style={styles.cardSubtitle}>Both candidate and parent/guardian digital signatures are legally binding for overseas council submission.</Text>
              </View>
            </View>

            <Text style={styles.label}>
              Candidate&apos;s Signature <Text style={styles.required}>*</Text> — Draw inside the box using mouse, stylus, or touch.
            </Text>
            <SignaturePad ref={candidatePad} colors={colors} hasError={!!errors.candidateSignature} />
            <View style={[styles.row, { justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }]}>
              <Text style={styles.hint}>Signer: {name || 'Candidate'}</Text>
              <TouchableOpacity onPress={() => { candidatePad.current?.clear(); setSignatureVersion(v => v + 1); }}>
                <Text style={styles.link}>Clear signature</Text>
              </TouchableOpacity>
            </View>
            {errors.candidateSignature && signatureVersion >= 0 ? <Text style={styles.errorText}>{errors.candidateSignature}</Text> : null}

            <Text style={styles.commitment}>
              <Text style={{ fontWeight: '700' }}>Parent / Guardian&apos;s commitment: </Text>
              I, {form.parentGuardianName.trim() || '________________'}, hereby acknowledge that my son/daughter/ward, {name || 'the candidate'}, will need to diligently prepare for CXC/CSEC examinations. I pledge to give all assistance and guidance to ensure adequate preparation, and will ensure all commitments financial and otherwise are met prior to council deadlines.
            </Text>
            <View style={styles.grid}>{textField('parentGuardianName', 'Parent / guardian name', { span: 2 })}</View>
            <Text style={[styles.label, { marginTop: Spacing.md }]}>Parent / guardian&apos;s signature</Text>
            <SignaturePad ref={parentPad} colors={colors} />
            <View style={[styles.row, { justifyContent: 'flex-end', marginTop: 4 }]}>
              <TouchableOpacity onPress={() => parentPad.current?.clear()}><Text style={styles.link}>Clear signature</Text></TouchableOpacity>
            </View>
          </View>

          {/* Section 8: Important Notes */}
          <View style={styles.card}>
            <View style={styles.cardHeaderWithIcon}>
              <View style={styles.cardIconBox}>
                <FeatherIcon name="info" size={18} color={colors.primary} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.cardTitle}>Important Examination Guidelines</Text>
                <Text style={styles.cardSubtitle}>Please read the official Cayman Academy CXC policy notes below carefully.</Text>
              </View>
            </View>

            {[
              'If the candidate sat CSEC subjects previously, register the exact legal name used on prior certificates.',
              'Original government identification (Passport or Birth Certificate) is required when submitting payment.',
              'Candidates will NOT be entered with the Caribbean Examinations Council until the voucher is stamped by the school finance office.',
              'Candidates and parents are responsible for reviewing timetable dates and attending scheduled practical and oral sessions.',
            ].map(note => <Text key={note} style={styles.note}>• {note}</Text>)}
          </View>

          {/* Error Summary */}
          {Object.keys(errors).length > 0 ? (
            <View style={styles.errorSummary}>
              <Text style={styles.errorSummaryTitle}>Please correct the following fields:</Text>
              {Object.values(errors).map(message => <Text key={message} style={styles.errorSummaryItem}>• {message}</Text>)}
            </View>
          ) : null}

          {/* Submit Action Row */}
          <View style={[styles.row, { justifyContent: 'flex-end', marginTop: Spacing.sm }]}>
            <TouchableOpacity style={styles.primaryBtn} onPress={handleReviewAndPrint}>
              <Text style={styles.primaryBtnText}>Submit CXC Registration</Text>
            </TouchableOpacity>
          </View>
        </>
      )}

      {/* Senior-Level Announcement Flyer Lightbox Modal */}
      <Modal
        visible={showPosterModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowPosterModal(false)}
      >
        <View style={styles.modalOverlay}>
          <TouchableOpacity
            style={StyleSheet.absoluteFill}
            activeOpacity={1}
            onPress={() => setShowPosterModal(false)}
          />
          <View style={styles.posterModalContent}>
            <View style={styles.posterModalHeader}>
              <View style={{ flex: 1 }}>
                <Text style={styles.posterModalTitle}>Official CXC Registration Announcement</Text>
                <Text style={styles.posterModalSubtitle}>
                  {CXC_CENTRE.name} • Overseas Centre No. {CXC_CENTRE.number}
                </Text>
              </View>
              <TouchableOpacity
                style={styles.modalCloseBtn}
                onPress={() => setShowPosterModal(false)}
                accessibilityLabel="Close flyer"
              >
                <FeatherIcon name="x" size={20} color={colors.textPrimary} />
              </TouchableOpacity>
            </View>
            <ScrollView contentContainerStyle={styles.posterModalBody} showsVerticalScrollIndicator={false}>
              <Image
                source={BANNER_IMAGE}
                style={styles.posterModalFullImage as any}
                resizeMode="contain"
              />
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Review & Print popup - shown on Submit; the public website's Save & Preview step, same chrome and document. */}
      {/* RN Modal portals to document.body: a position:fixed overlay rendered inside
          the page shell would be anchored to a transformed ancestor and scroll
          away with the page instead of covering the viewport. */}
      <Modal visible={showPreview} transparent animationType="fade" onRequestClose={() => { if (!submitting) { setShowPreview(false); setSubmitError(null); } }}>
        <CxcPreviewModal
          error={submitError}
          submitting={submitting}
          onClose={() => { if (!submitting) { setShowPreview(false); setSubmitError(null); } }}
          onPrint={handlePrint}
          onConfirm={handleConfirmSubmit}
        >
          {renderPrintDocument(previewDocRef)}
        </CxcPreviewModal>
      </Modal>

      {/* A past registration, opened from the Previous tab: the same document, read-only, printable. */}
      <Modal visible={!!viewing && !!viewingDocument} transparent animationType="fade" onRequestClose={() => setViewing(null)}>
        {viewing && viewingDocument ? (
          <CxcPreviewModal
            title="Registration Details"
            subtitle={
              <>
                Invoice <Text style={{ fontWeight: '700' }}>{viewing.invoiceNumber}</Text>
                {' • '}Submitted {new Date(viewing.createdDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                {viewing.totalBalance != null && viewing.totalBalance > 0 ? ` • Balance ${formatMoney(viewing.totalBalance)} KYD` : ''}
                {'  '}
                <CxcStatusPill
                  label={cxcStatusLabel(viewing)}
                  tone={viewing.status === 'RegisteredWithCxc' ? 'done' : viewing.status === 'Paid' ? 'paid' : 'pending'}
                />
              </>
            }
            onClose={() => setViewing(null)}
            onPrint={handlePrintViewing}
          >
            {viewingDocument}
          </CxcPreviewModal>
        ) : null}
      </Modal>

      {/* Mounted after the review popup so its portal stacks above it while the submit is in flight. */}
      <SubmittingOverlay
        visible={submitting}
        title="Submitting your registration"
        message="Sending it to Cayman Academy and raising your invoice. Please keep this window open - it only takes a moment."
      />

      <Toast visible={toast.visible} message={toast.message} type={toast.type} onDismiss={() => setToast(t => ({ ...t, visible: false }))} />
    </WebPageShell>
  );
}

const createStyles = (colors: ThemeColors, isDark: boolean = false, isMobile: boolean = false) =>
  StyleSheet.create({
    backRow: { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', marginBottom: Spacing.sm, paddingVertical: 4 },
    backRowText: { marginLeft: 8, fontSize: FontSize.sm, fontWeight: '600', color: colors.primary },

    // Senior Enterprise Hero Banner
    heroBanner: {
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'stretch' : 'center',
      gap: Spacing.lg,
      backgroundColor: isDark ? '#1E293B' : colors.primary,
      borderRadius: BorderRadius.lg,
      padding: isMobile ? Spacing.md : Spacing.xl,
      marginBottom: Spacing.md,
      borderWidth: 1,
      borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.12,
      shadowRadius: 16,
      elevation: 4,
    },
    heroLeftCol: { flex: 1, minWidth: 0 },
    heroRightCol: { width: isMobile ? '100%' : 240, alignItems: 'center', justifyContent: 'center' },

    heroAccreditationPill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      backgroundColor: 'rgba(245, 158, 11, 0.22)',
      borderWidth: 1,
      borderColor: 'rgba(245, 158, 11, 0.45)',
      paddingHorizontal: 12,
      paddingVertical: 5,
      borderRadius: 999,
      alignSelf: 'flex-start',
      marginBottom: Spacing.sm,
    },
    heroAccreditationText: {
      color: '#FDE68A',
      fontSize: FontSize.xs,
      fontWeight: '700',
      letterSpacing: 0.4,
      textTransform: 'uppercase',
    },
    heroTitle: {
      color: colors.textInverse,
      fontSize: isMobile ? FontSize.xl : FontSize.xxl,
      fontWeight: '800',
      letterSpacing: -0.3,
      marginBottom: 6,
    },
    heroSubtitle: {
      color: colors.textInverse,
      opacity: 0.9,
      fontSize: FontSize.sm,
      lineHeight: 20,
      marginBottom: Spacing.md,
    },

    // Highlights inside Header
    highlightsContainer: {
      flexDirection: 'column',
      gap: 8,
      marginTop: 4,
    },
    highlightCard: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: Spacing.sm,
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
      borderWidth: 1,
      borderColor: 'rgba(255, 255, 255, 0.2)',
      borderRadius: BorderRadius.md,
      paddingHorizontal: Spacing.sm + 4,
      paddingVertical: 8,
    },
    highlightWarningCard: {
      backgroundColor: 'rgba(245, 158, 11, 0.15)',
      borderColor: 'rgba(245, 158, 11, 0.35)',
    },
    highlightIconBox: {
      width: 28,
      height: 28,
      borderRadius: 6,
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    highlightLabel: {
      color: '#FDE68A',
      fontSize: 11,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.3,
    },
    highlightValue: {
      color: colors.textInverse,
      fontSize: FontSize.xs + 1,
      fontWeight: '600',
    },
    highlightBadge: {
      backgroundColor: 'rgba(16, 185, 129, 0.25)',
      borderWidth: 1,
      borderColor: 'rgba(16, 185, 129, 0.5)',
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: 999,
    },
    highlightBadgeText: {
      color: '#D1FAE5',
      fontSize: 10,
      fontWeight: '700',
      textTransform: 'uppercase',
    },
    highlightCardClosed: {
      backgroundColor: 'rgba(239, 68, 68, 0.12)',
      borderColor: 'rgba(239, 68, 68, 0.3)',
    },
    highlightIconBoxClosed: {
      backgroundColor: 'rgba(239, 68, 68, 0.2)',
    },
    highlightLabelClosed: {
      color: '#FCA5A5',
    },
    highlightValueClosed: {
      color: '#FECACA',
    },
    highlightBadgeClosed: {
      backgroundColor: 'rgba(239, 68, 68, 0.3)',
      borderColor: 'rgba(239, 68, 68, 0.6)',
    },
    highlightBadgeTextClosed: {
      color: '#FEE2E2',
    },

    // Flyer Preview Showcase Card
    flyerCard: {
      width: '100%',
      maxWidth: 240,
      borderRadius: 14,
      overflow: 'hidden',
      borderWidth: 1.5,
      borderColor: 'rgba(255, 255, 255, 0.35)',
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 6 },
      shadowOpacity: 0.25,
      shadowRadius: 14,
      elevation: 6,
    },
    flyerBadgeOverlay: {
      position: 'absolute',
      top: 8,
      left: 8,
      zIndex: 2,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      backgroundColor: 'rgba(15, 23, 42, 0.85)',
      borderWidth: 1,
      borderColor: 'rgba(245, 158, 11, 0.4)',
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 999,
    },
    pulseDot: {
      width: 6,
      height: 6,
      borderRadius: 3,
      backgroundColor: '#22C55E',
    },
    flyerBadgeText: {
      color: '#FDE68A',
      fontSize: 10,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.3,
    },
    flyerImage: {
      width: '100%',
      height: 220,
    },
    flyerFooter: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 10,
      paddingVertical: 6,
      backgroundColor: 'rgba(15, 23, 42, 0.75)',
    },
    flyerFooterLeft: { flexDirection: 'row', alignItems: 'center', gap: 4 },
    flyerFooterNoticeText: { color: '#E2E8F0', fontSize: 11, fontWeight: '500' },
    flyerFooterRight: { flexDirection: 'row', alignItems: 'center', gap: 4 },
    flyerExpandText: { color: '#FFFFFF', fontSize: 11, fontWeight: '700' },

    // Lightbox Modal
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(10, 20, 38, 0.85)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: Spacing.md,
    },
    posterModalContent: {
      width: '100%',
      maxWidth: 620,
      maxHeight: '90%',
      backgroundColor: colors.surface,
      borderRadius: BorderRadius.lg,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: colors.border,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 12 },
      shadowOpacity: 0.35,
      shadowRadius: 24,
      elevation: 10,
    },
    posterModalHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: Spacing.lg,
      paddingVertical: Spacing.md,
      backgroundColor: colors.card,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    posterModalTitle: {
      fontSize: FontSize.md,
      fontWeight: '700',
      color: colors.textPrimary,
    },
    posterModalSubtitle: {
      fontSize: FontSize.xs,
      color: colors.textSecondary,
      marginTop: 2,
    },
    modalCloseBtn: {
      width: 34,
      height: 34,
      borderRadius: 8,
      backgroundColor: colors.divider,
      alignItems: 'center',
      justifyContent: 'center',
    },
    posterModalBody: {
      padding: Spacing.md,
      alignItems: 'center',
      justifyContent: 'center',
    },
    posterModalFullImage: {
      width: '100%',
      height: 640,
    },

    hiddenPrintHost: {
      position: 'absolute',
      opacity: 0,
      pointerEvents: 'none',
      top: -99999,
      left: -99999,
    },

    tabs: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: colors.border, marginBottom: Spacing.md },
    tab: { paddingVertical: Spacing.sm, paddingHorizontal: Spacing.md, borderBottomWidth: 2, borderBottomColor: 'transparent' },
    tabActive: { borderBottomColor: colors.primary },
    tabText: { fontSize: FontSize.sm, fontWeight: '600', color: colors.textSecondary },
    tabTextActive: { color: colors.primary },
    card: { backgroundColor: colors.card, borderRadius: BorderRadius.lg, padding: Spacing.lg, borderWidth: 1, borderColor: colors.border, marginBottom: Spacing.md },
    cardHeaderWithIcon: { flexDirection: 'row', alignItems: 'flex-start', gap: Spacing.sm + 2, marginBottom: Spacing.sm },
    cardIconBox: {
      width: 34,
      height: 34,
      borderRadius: 8,
      backgroundColor: colors.primaryLight,
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    cardTitle: { fontSize: FontSize.md, fontWeight: '700', color: colors.textPrimary, marginBottom: 2 },
    cardSubtitle: { fontSize: FontSize.sm, color: colors.textSecondary, marginBottom: Spacing.xs },
    row: { flexDirection: 'row', flexWrap: 'wrap', gap: Spacing.sm },
    grid: { flexDirection: 'row', flexWrap: 'wrap', gap: Spacing.md },
    field: { width: '31%', minWidth: 220, flexGrow: 1 },
    span2: { width: '64%' },
    span3: { width: '100%' },
    label: { fontSize: FontSize.sm, fontWeight: '500', color: colors.textPrimary, marginBottom: 4 },
    required: { color: colors.danger, fontWeight: '700' },
    hint: { fontSize: FontSize.xs, color: colors.textSecondary, marginTop: 4 },
    errorText: { fontSize: FontSize.xs, color: colors.danger, marginTop: 4 },
    link: { fontSize: FontSize.sm, color: colors.primary, fontWeight: '600', marginTop: Spacing.xs, textDecorationLine: 'underline' },
    chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: Spacing.xs },
    chip: { paddingVertical: 8, paddingHorizontal: 14, borderRadius: 999, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.surface },
    chipActive: { backgroundColor: colors.primary, borderColor: colors.primary },
    chipDisabled: { opacity: 0.45 },
    chipText: { fontSize: FontSize.sm, fontWeight: '600', color: colors.textSecondary },
    chipTextActive: { color: colors.textInverse },
    sittingCard: { flexDirection: 'row', alignItems: 'center', padding: Spacing.sm + 4, borderRadius: BorderRadius.md, borderWidth: 1.5, borderColor: colors.border, backgroundColor: colors.surface, minWidth: 260, flexGrow: 1 },
    sittingCardActive: { borderColor: colors.primary, backgroundColor: colors.primaryLight },
    sittingLabel: { fontSize: FontSize.md, fontWeight: '700', color: colors.textPrimary },
    sittingStatusTag: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 999, backgroundColor: colors.divider },
    sittingStatusTagActive: { backgroundColor: colors.primary },
    sittingStatusTagText: { fontSize: 11, fontWeight: '700', color: colors.textSecondary },
    sittingStatusTagTextActive: { color: colors.textInverse },

    sittingCardClosed: {
      backgroundColor: '#FEF2F2',
      borderColor: '#FCA5A5',
      opacity: 0.92,
    },
    sittingLabelClosed: {
      color: '#991B1B',
    },
    hintClosed: {
      color: '#DC2626',
      fontWeight: '600',
    },
    closedPillBadge: {
      backgroundColor: '#DC2626',
      paddingHorizontal: 6,
      paddingVertical: 1,
      borderRadius: 4,
    },
    closedPillBadgeText: {
      color: '#FFFFFF',
      fontSize: 9,
      fontWeight: '800',
      letterSpacing: 0.4,
    },
    sittingStatusTagClosed: {
      backgroundColor: '#FEE2E2',
      borderWidth: 1,
      borderColor: '#F87171',
    },
    sittingStatusTagTextClosed: {
      color: '#DC2626',
      fontWeight: '800',
    },

    // Partial Deadline Notice
    partialDeadlineNotice: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      backgroundColor: '#FFFBEB',
      borderWidth: 1,
      borderColor: '#FCD34D',
      borderRadius: BorderRadius.md,
      padding: Spacing.md,
      marginBottom: Spacing.md,
    },
    partialDeadlineNoticeTitle: {
      fontSize: FontSize.sm,
      fontWeight: '700',
      color: '#B45309',
    },
    partialDeadlineNoticeText: {
      fontSize: FontSize.xs + 1,
      color: '#92400E',
      marginTop: 2,
    },

    // Executive Closed Window Card (When all deadlines have passed)
    closedWindowCard: {
      backgroundColor: colors.card,
      borderRadius: BorderRadius.lg,
      borderWidth: 1.5,
      borderColor: '#FCA5A5',
      padding: Spacing.xl,
      marginBottom: Spacing.lg,
      shadowColor: '#DC2626',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.08,
      shadowRadius: 16,
      elevation: 4,
    },
    closedWindowHeader: {
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'flex-start' : 'center',
      gap: Spacing.md,
      marginBottom: Spacing.md,
    },
    closedWindowIconBox: {
      width: 52,
      height: 52,
      borderRadius: 16,
      backgroundColor: '#FEE2E2',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    closedHeaderRow: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 8,
      marginBottom: 4,
    },
    closedWindowTitle: {
      fontSize: FontSize.lg,
      fontWeight: '800',
      color: colors.textPrimary,
    },
    closedBadge: {
      backgroundColor: '#EF4444',
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: 6,
    },
    closedBadgeText: {
      color: '#FFFFFF',
      fontSize: 10,
      fontWeight: '800',
      letterSpacing: 0.5,
    },
    closedWindowSubtitle: {
      fontSize: FontSize.sm,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    closedDeadlinesSummaryBox: {
      backgroundColor: '#FEF2F2',
      borderRadius: BorderRadius.md,
      borderWidth: 1,
      borderColor: '#FECACA',
      padding: Spacing.md,
      marginBottom: Spacing.md,
      gap: 6,
    },
    closedSummaryHeading: {
      fontSize: FontSize.xs + 1,
      fontWeight: '700',
      color: '#991B1B',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      marginBottom: 2,
    },
    closedDeadlineSummaryRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    closedDeadlineSummaryText: {
      fontSize: FontSize.sm,
      color: '#7F1D1D',
    },
    closedOfficeNoticeBox: {
      backgroundColor: colors.background,
      borderRadius: BorderRadius.md,
      borderWidth: 1,
      borderColor: colors.border,
      padding: Spacing.md,
      marginBottom: Spacing.md,
    },
    closedOfficeHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      marginBottom: 4,
    },
    closedOfficeTitle: {
      fontSize: FontSize.sm,
      fontWeight: '700',
      color: colors.textPrimary,
    },
    closedOfficeBody: {
      fontSize: FontSize.xs + 1,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    closedActionsRow: {
      flexDirection: 'row',
      justifyContent: isMobile ? 'center' : 'flex-start',
      marginTop: 4,
    },

    prefilledBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      backgroundColor: colors.successLight,
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: 999,
    },
    prefilledBadgeText: { color: colors.success, fontSize: 11, fontWeight: '600' },

    // Photo Studio (Senior Enterprise)
    photoStudioContainer: {
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'center' : 'flex-start',
      gap: Spacing.lg,
      marginTop: Spacing.sm,
      backgroundColor: colors.background,
      borderRadius: BorderRadius.md,
      padding: Spacing.md,
      borderWidth: 1,
      borderColor: colors.border,
    },
    photoFrameWrapper: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    photoFrame: {
      width: 145,
      height: 185,
      borderRadius: 12,
      borderWidth: 2,
      borderStyle: 'dashed',
      borderColor: colors.border,
      backgroundColor: colors.surface,
      overflow: 'hidden',
      position: 'relative',
      alignItems: 'center',
      justifyContent: 'center',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 3 },
      shadowOpacity: 0.08,
      shadowRadius: 8,
      elevation: 2,
    },
    photoFrameActive: {
      borderStyle: 'solid',
      borderColor: colors.primary,
    },
    photoEmptyPlaceholder: {
      alignItems: 'center',
      justifyContent: 'center',
      padding: Spacing.sm,
      width: '100%',
      height: '100%',
    },
    photoGuideSilhouette: {
      width: 60,
      height: 60,
      borderRadius: 30,
      backgroundColor: colors.divider,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 6,
    },
    photoGuideText: {
      fontSize: 12,
      fontWeight: '700',
      color: colors.textPrimary,
    },
    photoGuideSubtext: {
      fontSize: 11,
      color: colors.textMuted,
      marginBottom: 8,
    },
    photoFrameTagRequired: {
      backgroundColor: colors.dangerLight,
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: 999,
    },
    photoFrameTagRequiredText: {
      color: colors.danger,
      fontSize: 10,
      fontWeight: '700',
    },
    photoFrameTagVerified: {
      position: 'absolute',
      bottom: 6,
      left: 6,
      right: 6,
      backgroundColor: 'rgba(16, 185, 129, 0.9)',
      paddingVertical: 3,
      paddingHorizontal: 6,
      borderRadius: 6,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
    },
    photoFrameTagText: {
      color: '#FFFFFF',
      fontSize: 10,
      fontWeight: '700',
    },
    photoRightCol: {
      flex: 1,
      minWidth: isMobile ? '100%' : 260,
    },
    photoActionButtonsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: Spacing.sm,
      marginBottom: Spacing.sm,
    },
    primaryPhotoBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      backgroundColor: colors.primary,
      paddingVertical: 10,
      paddingHorizontal: 16,
      borderRadius: 8,
    },
    primaryPhotoBtnText: {
      color: colors.textInverse,
      fontSize: FontSize.sm,
      fontWeight: '600',
    },
    schoolPhotoBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      backgroundColor: colors.primaryLight,
      borderWidth: 1,
      borderColor: colors.primary,
      paddingVertical: 9,
      paddingHorizontal: 14,
      borderRadius: 8,
    },
    schoolPhotoBtnText: {
      color: colors.primary,
      fontSize: FontSize.sm,
      fontWeight: '600',
    },
    removePhotoBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      backgroundColor: colors.dangerLight,
      borderWidth: 1,
      borderColor: colors.danger,
      paddingVertical: 9,
      paddingHorizontal: 12,
      borderRadius: 8,
    },
    removePhotoBtnText: {
      color: colors.danger,
      fontSize: FontSize.xs + 1,
      fontWeight: '600',
    },
    photoErrorPill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      backgroundColor: colors.dangerLight,
      borderWidth: 1,
      borderColor: colors.danger,
      paddingHorizontal: Spacing.sm,
      paddingVertical: 6,
      borderRadius: 6,
      marginBottom: Spacing.sm,
    },
    photoErrorPillText: {
      color: colors.danger,
      fontSize: FontSize.xs + 1,
      fontWeight: '600',
    },
    idPdfTile: {
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      paddingHorizontal: Spacing.md,
    },
    idPdfNameRow: {
      flexDirection: 'row',
      justifyContent: 'center',
      maxWidth: '100%',
    },
    idPdfName: {
      fontSize: FontSize.sm,
      fontWeight: '600',
      color: colors.textPrimary,
    },
    // The name gives way first; the extension never does, so a long name still ends in .pdf
    idPdfNameBase: {
      flexShrink: 1,
    },
    idPdfMetaRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    idPdfBadge: {
      paddingHorizontal: 6,
      paddingVertical: 1,
      borderRadius: 4,
      backgroundColor: colors.primaryLight,
    },
    idPdfBadgeText: {
      fontSize: FontSize.xs - 1,
      fontWeight: '700',
      color: colors.primary,
      letterSpacing: 0.4,
    },
    idPdfMeta: {
      fontSize: FontSize.xs,
      color: colors.textSecondary,
    },
    idDocumentFrameWrapper: {
      width: 260,
      maxWidth: '100%',
    },
    idDocumentFrame: {
      width: '100%',
      height: 170,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      overflow: 'hidden',
      alignItems: 'center',
      justifyContent: 'center',
    },
    idDocumentWarnPill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      backgroundColor: colors.warningLight,
      borderWidth: 1,
      borderColor: colors.warning,
      paddingHorizontal: Spacing.sm,
      paddingVertical: 6,
      borderRadius: 6,
      marginBottom: Spacing.sm,
    },
    idDocumentWarnPillText: {
      color: colors.textPrimary,
      fontSize: FontSize.xs + 1,
      fontWeight: '600',
      flex: 1,
    },
    photoChecklistCard: {
      backgroundColor: colors.surface,
      borderRadius: 8,
      padding: Spacing.sm + 4,
      borderWidth: 1,
      borderColor: colors.border,
    },
    photoChecklistHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      marginBottom: 8,
    },
    photoChecklistTitle: {
      fontSize: FontSize.xs + 1,
      fontWeight: '700',
      color: colors.textPrimary,
      textTransform: 'uppercase',
      letterSpacing: 0.3,
    },
    photoChecklistItems: {
      flexDirection: 'column',
      gap: 5,
    },
    photoChecklistItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    photoChecklistText: {
      fontSize: FontSize.xs,
      color: colors.textSecondary,
      lineHeight: 16,
    },

    searchInput: { height: 40, minWidth: 240, flexGrow: 1, borderWidth: 1, borderColor: colors.border, borderRadius: BorderRadius.md, paddingHorizontal: Spacing.sm + 2, color: colors.textPrimary, backgroundColor: colors.surface },
    subjectCountBadge: {
      backgroundColor: colors.primaryLight,
      borderWidth: 1,
      borderColor: colors.primary,
      paddingHorizontal: 10,
      paddingVertical: 3,
      borderRadius: 999,
    },
    subjectCountBadgeText: {
      color: colors.primary,
      fontSize: FontSize.xs,
      fontWeight: '700',
    },
    subjectRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: Spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.divider },
    subjectHead: { marginTop: Spacing.sm, borderBottomColor: colors.border },
    subjectHeadText: { fontSize: FontSize.xs, fontWeight: '700', color: colors.textSecondary, letterSpacing: 0.5 },
    subjectRowSelected: { backgroundColor: colors.primaryLight, marginHorizontal: -Spacing.lg, paddingHorizontal: Spacing.lg },
    subjectName: { fontSize: FontSize.md, color: colors.textPrimary, fontWeight: '500' },
    sbaWarningTag: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      marginTop: 2,
    },
    sbaWarningTagText: {
      fontSize: 11,
      color: colors.warning,
      fontWeight: '600',
    },
    proficiencyTag: { fontSize: FontSize.xs, fontWeight: '700', color: colors.textSecondary },
    proficiencyCape: { color: colors.warning },
    feeRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: colors.divider },
    feeLabel: { flex: 1, fontSize: FontSize.sm, color: colors.textPrimary, paddingRight: Spacing.sm },
    feeAmount: { fontSize: FontSize.sm, color: colors.textPrimary, fontWeight: '600' },
    feeMuted: { color: colors.textMuted },
    feeTotalRow: {
      borderBottomWidth: 0,
      borderTopWidth: 2,
      borderTopColor: colors.border,
      marginTop: 6,
      paddingTop: 12,
      backgroundColor: colors.primaryLight,
      marginHorizontal: -Spacing.lg,
      paddingHorizontal: Spacing.lg,
      borderRadius: 8,
    },
    feeTotalLabel: { fontSize: FontSize.md, fontWeight: '700', color: colors.textPrimary },
    feeTotalSubtitle: { fontSize: FontSize.xs, color: colors.textSecondary, marginTop: 2 },
    feeTotalAmount: { fontSize: FontSize.lg, fontWeight: '800', color: colors.primary },
    commitment: { fontSize: FontSize.sm, lineHeight: 22, color: colors.textPrimary, marginVertical: Spacing.md, backgroundColor: colors.background, padding: Spacing.sm + 4, borderRadius: BorderRadius.md, borderWidth: 1, borderColor: colors.border },
    note: { fontSize: FontSize.sm, color: colors.textSecondary, lineHeight: 20, marginTop: 4 },
    errorSummary: { backgroundColor: colors.dangerLight, borderRadius: BorderRadius.md, padding: Spacing.md, marginBottom: Spacing.md, borderWidth: 1, borderColor: colors.danger },
    errorSummaryTitle: { color: colors.danger, fontWeight: '700', marginBottom: 4 },
    errorSummaryItem: { color: colors.danger, fontSize: FontSize.sm, lineHeight: 20 },
    primaryBtn: { backgroundColor: colors.primary, borderRadius: BorderRadius.md, paddingVertical: 12, paddingHorizontal: 28, alignItems: 'center', shadowColor: colors.primary, shadowOffset: { width: 0, height: 3 }, shadowOpacity: 0.25, shadowRadius: 6, elevation: 3 },
    primaryBtnText: { color: colors.textInverse, fontWeight: '700', fontSize: FontSize.md },
    secondaryBtn: { borderWidth: 1, borderColor: colors.primary, borderRadius: BorderRadius.md, paddingVertical: 12, paddingHorizontal: 24, alignItems: 'center' },
    secondaryBtnText: { color: colors.primary, fontWeight: '600', fontSize: FontSize.md },
    successIcon: { width: 60, height: 60, borderRadius: 30, backgroundColor: colors.successLight, alignItems: 'center', justifyContent: 'center', marginBottom: Spacing.sm },
    successTitle: { fontSize: FontSize.lg, fontWeight: '700', color: colors.textPrimary },
    invoiceBox: { marginVertical: Spacing.md, paddingVertical: Spacing.sm, paddingHorizontal: Spacing.lg, borderRadius: BorderRadius.md, backgroundColor: colors.primaryLight, alignItems: 'center' },
    invoiceLabel: { fontSize: FontSize.xs, fontWeight: '700', color: colors.primary, letterSpacing: 1 },
    invoiceNumber: { fontSize: 26, fontWeight: '800', color: colors.textPrimary, letterSpacing: 1 },
    receiptRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: Spacing.sm + 2, paddingHorizontal: Spacing.xs, borderBottomWidth: 1, borderBottomColor: colors.divider, gap: Spacing.sm, borderRadius: 6 },
    receiptCell: { fontSize: FontSize.sm, color: colors.textPrimary },
    receiptInvoice: { fontWeight: '700' },
    statusPill: { fontSize: FontSize.xs, fontWeight: '700', color: colors.warning, backgroundColor: colors.warningLight, paddingHorizontal: 8, paddingVertical: 2, borderRadius: 999, overflow: 'hidden' },
  });
